import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useValidator } from '../utils/useValidator';
import { useSetting } from '../utils/AppSettingsContext';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getPaginatedGlobalGroupsCombined, getGlobalGroupById } from '../api/globalGroupAPI';
import { getGroupById } from '../api/groupAPI';
import { createAsset, getAssetBySerial } from '../api/assetAPI';
import { createAssetMeter } from '../api/asset-meterAPI';
import { createMeterReadings } from '../api/meter-readingsAPI';
import { getMeterById } from '../api/metersAPI';
import { getHierarchyStructureById } from '../api/hierarchy-structureAPI';
import { clearCache } from '../utils/lookupCache';
import { showToastError, showToastSuccess } from '../utils/errorController';

const buildRow = (startDate = '') => ({
  assetNumber: '',
  assetSerial: '',
  assetStartDate: startDate,
  assetEndDate: '',
});

const getTodayDateString = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const normalizeDate = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const toNumericOrNull = (value) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
};

const AssetBulkAddModal = ({ show, onClose, onSaved, assetTypes = [], planningTypes = [] }) => {
  const [formData, setFormData] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [rows, setRows] = useState([buildRow(getTodayDateString())]);
  const [rowErrors, setRowErrors] = useState([{}]);
  const [saving, setSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [serialRequired, setSerialRequired] = useState(false);
  const [noDuplicateSerial, setNoDuplicateSerial] = useState(false);
  const [defaultMeterId, setDefaultMeterId] = useState(null);
  const [defaultUtilisation, setDefaultUtilisation] = useState('0');
  const [meterDetails, setMeterDetails] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [pendingPayloads, setPendingPayloads] = useState([]);
  const [pendingMeterConfig, setPendingMeterConfig] = useState({
    startReading: 0,
    utilisation: 0,
  });
  const [confirmationRows, setConfirmationRows] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const userProfile = useUserProfile();
  const defaultCostCentre = useSetting('default_cost_centre');
  const defaultCostCentreId =
    defaultCostCentre !== null && defaultCostCentre !== undefined
      ? Number(defaultCostCentre) || null
      : null;

  useEffect(() => {
    if (!show) return;
    setFormData({
      plantStatusId: 6,
      assetCriticalityId: 2,
      costCenterId: defaultCostCentreId,
      meterStartReading: '0',
      meterUtilisation: '0',
      assetImage: '',
      groupId: null,
      AssetTypeId: null,
      PlanningTypeId: null,
      assetDepartmentId: null,
      lifeInMeterUnits: '',
    });
    setSelectedGroup(null);
    setSelectedDepartment(null);
    setRows([buildRow(getTodayDateString())]);
    setRowErrors([{}]);
    setSaving(false);
    setIsValidating(false);
    setSerialRequired(false);
    setNoDuplicateSerial(false);
    setDefaultMeterId(null);
    setDefaultUtilisation('0');
    setMeterDetails(null);
    setPendingPayloads([]);
    setPendingMeterConfig({ startReading: 0, utilisation: 0 });
    setConfirmationRows([]);
    setShowConfirmationModal(false);
  }, [show, defaultCostCentreId]);

  const assetTypeOptions = useMemo(
    () =>
      (Array.isArray(assetTypes) ? assetTypes : []).map((type) => ({
        value: type.id,
        label: type.name,
        data: type,
      })),
    [assetTypes],
  );

  const planningTypeOptions = useMemo(
    () =>
      (Array.isArray(planningTypes) ? planningTypes : []).map((type) => ({
        value: type.id,
        label: type.typeDescription,
      })),
    [planningTypes],
  );

  const handleGroupSearch = useCallback(async (inputValue) => {
    try {
      const raw = typeof inputValue === 'string' ? inputValue : '';
      const trimmed = raw.trim();
      const shouldLoadDefault = trimmed.length === 0;
      if (!shouldLoadDefault && trimmed.length < 2) {
        return [];
      }
      const resp = await getPaginatedGlobalGroupsCombined({
        page: 1,
        pageSize: 10,
        search: shouldLoadDefault ? '' : trimmed,
      });
      const list = Array.isArray(resp?.data)
        ? resp.data
        : Array.isArray(resp?.results)
          ? resp.results
          : Array.isArray(resp)
            ? resp
            : [];
      return list.map((g) => ({
        value: g.id,
        label: `${g.globalManufacturer?.manufacturer || g.manufacturer?.manufacturer || ''} ${g.groupName || ''} ${g.globalClass?.className || g.class?.className || ''}`.trim(),
        isLocal: g.isLocal ?? !(g.globalManufacturer || g.globalClass || g.globalGroupId),
      }));
    } catch (err) {
      console.error('Error fetching groups:', err);
      return [];
    }
  }, []);

  const handleGroupChange = useCallback((opt, setFormState) => {
    setSelectedGroup(opt || null);
    setFormState((prev) => ({
      ...prev,
      groupId: opt?.value || null,
    }));
  }, []);

  useEffect(() => {
    if (!formData.groupId) return;
    (async () => {
      try {
        const grp =
          selectedGroup?.isLocal === false
            ? await getGlobalGroupById(formData.groupId)
            : await getGroupById(formData.groupId);
        const label = `${grp.globalManufacturer?.manufacturer || grp.manufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.globalClass?.className || grp.class?.className || ''}`.trim();
        setFormData((prev) => ({
          ...prev,
          assetDescription: label,
          assetImage: grp.groupImage || prev.assetImage,
        }));
        setSelectedGroup((prev) => ({
          value: grp.id,
          label,
          isLocal:
            prev?.isLocal === false
              ? false
              : grp.isLocal ?? !(grp.globalManufacturer || grp.globalClass || grp.globalGroupId),
        }));
      } catch (err) {
        console.error('Error loading group details:', err);
      }
    })();
  }, [formData.groupId, selectedGroup?.isLocal]);

  useEffect(() => {
    const div = document.getElementById('imageBlock1');
    if (!div) return;

    if (formData.assetImage) {
      div.style.backgroundImage = `url(${process.env.REACT_APP_SPACES_BASE}/${formData.assetImage})`;
      div.style.backgroundSize = 'cover';
      div.style.backgroundPosition = 'center';
    } else {
      div.style.backgroundImage = '';
    }
  }, [formData.assetImage]);

  useEffect(() => {
    const selectedOption = assetTypeOptions.find((opt) => opt.value === formData.AssetTypeId);
    const selectedType = selectedOption?.data;
    if (selectedType) {
      setSerialRequired(selectedType.mandatorySerialNumber === 1);
      setNoDuplicateSerial(selectedType.noDuplicateSerialNumbers === 1);
      setDefaultMeterId(
        selectedType.defaultMeterId ? Number(selectedType.defaultMeterId) : null,
      );
      const util = selectedType.default_utilisation ?? selectedType.defaultUtilisation;
      setDefaultUtilisation(
        util !== undefined && util !== null ? String(util) : '0',
      );
      if (
        selectedType.defaultPlanningType &&
        selectedType.defaultPlanningType !== formData.PlanningTypeId
      ) {
        setFormData((prev) => ({
          ...prev,
          PlanningTypeId: selectedType.defaultPlanningType,
        }));
      }
    } else {
      setSerialRequired(false);
      setNoDuplicateSerial(false);
      setDefaultMeterId(null);
      setDefaultUtilisation('0');
    }
  }, [assetTypeOptions, formData.AssetTypeId, formData.PlanningTypeId]);

  useEffect(() => {
    if (defaultMeterId && (!formData.meterStartReading || !formData.meterUtilisation)) {
      setFormData((prev) => ({
        ...prev,
        meterStartReading: prev.meterStartReading ?? '0',
        meterUtilisation: prev.meterUtilisation ?? defaultUtilisation ?? '0',
      }));
    }
  }, [defaultMeterId, defaultUtilisation, formData.meterStartReading, formData.meterUtilisation]);

  useEffect(() => {
    if (!defaultMeterId) {
      setMeterDetails(null);
      return;
    }
    let isCancelled = false;
    (async () => {
      try {
        const meter = await getMeterById(defaultMeterId);
        if (!isCancelled) {
          setMeterDetails(meter || null);
        }
      } catch (err) {
        console.error('Error loading default meter details:', err);
        if (!isCancelled) {
          setMeterDetails(null);
        }
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [defaultMeterId]);

  useEffect(() => {
    if (!formData.assetDepartmentId) {
      setSelectedDepartment(null);
      return;
    }
    let isMounted = true;
    (async () => {
      try {
        const dept = await getHierarchyStructureById(formData.assetDepartmentId);
        if (isMounted) {
          setSelectedDepartment(dept || null);
        }
      } catch (err) {
        console.error('Error loading department details:', err);
        if (isMounted) {
          setSelectedDepartment(null);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [formData.assetDepartmentId]);

  const calculateEndDateFromLife = useCallback(
    (startDate) => {
      const lifeValue = Number(formData.lifeInMeterUnits);
      const utilisationValue = Number(
        formData.meterUtilisation || defaultUtilisation || 0,
      );
      if (
        !startDate ||
        !Number.isFinite(lifeValue) ||
        lifeValue <= 0 ||
        !Number.isFinite(utilisationValue) ||
        utilisationValue <= 0
      ) {
        return '';
      }
      const start = new Date(startDate);
      if (Number.isNaN(start.getTime())) {
        return '';
      }
      const years = lifeValue / utilisationValue;
      if (!Number.isFinite(years) || years <= 0) {
        return '';
      }
      const msToAdd = years * 365.25 * 24 * 60 * 60 * 1000;
      const endDate = new Date(start.getTime() + msToAdd);
      return normalizeDate(endDate.toISOString());
    },
    [formData.lifeInMeterUnits, formData.meterUtilisation, defaultUtilisation],
  );

  useEffect(() => {
    if (!formData.lifeInMeterUnits) return;
    const updatedIndexes = [];
    setRows((prev) =>
      prev.map((row, idx) => {
        if (!row.assetStartDate) return row;
        const calculated = calculateEndDateFromLife(row.assetStartDate);
        if (!calculated || calculated === row.assetEndDate) {
          return row;
        }
        updatedIndexes.push(idx);
        return { ...row, assetEndDate: calculated };
      }),
    );
    if (updatedIndexes.length > 0) {
      const clearSet = new Set(updatedIndexes);
      setRowErrors((prev) =>
        prev.map((err = {}, idx) => {
          if (!clearSet.has(idx) || !err.assetEndDate) return err;
          const { assetEndDate, ...rest } = err;
          return rest;
        }),
      );
    }
  }, [formData.lifeInMeterUnits, formData.meterUtilisation, calculateEndDateFromLife]);

  const commonFields = useMemo(() => {
    const fields = [
      {
        field: 'groupId',
        label: 'Model or Group',
        type: 'paginated-select',
        size: 4,
        loadOptions: handleGroupSearch,
        selectedOption: selectedGroup,
        required: true,
        onChange: handleGroupChange,
        minSearchLength: 0,
      },
      {
        field: 'AssetTypeId',
        label: 'Asset Type',
        type: 'select',
        size: 4,
        options: assetTypeOptions,
        required: true,
      },
      {
        field: 'PlanningTypeId',
        label: 'Planning Type',
        type: 'select',
        size: 4,
        options: planningTypeOptions,
        required: true,
      },
      {
        field: 'assetDepartmentId',
        label: 'Department',
        type: 'select',
        size: 4,
        api: 'hierarchy-structures',
        displayField: 'name',
        required: true,
      },
      {
        field: 'costCenterId',
        label: 'Cost Centre',
        type: 'select',
        size: 4,
        api: 'cost-centres',
        displayField: 'costCenterDescription',
        required: true,
      },
      {
        field: 'plantStatusId',
        label: 'Status',
        type: 'select',
        size: 4,
        api: 'plant-status',
        displayField: 'status',
        required: true,
      },
      {
        field: 'assetCriticalityId',
        label: 'Criticality',
        type: 'select',
        size: 4,
        api: 'asset-criticality',
        displayField: 'criticalityName',
        required: true,
      },
    ];
    fields.push(
      {
        field: 'assetImage',
        label: 'Asset Image',
        type: 'image',
        size: 4,
        directory: 'modelimages',
        showImage: false,
      },
      {
        field: 'imageBlock1',
        label: 'Image',
        type: 'emptyBorderSm',
        size: 4,
        required: false,
      },
    );
    if (defaultMeterId) {
      fields.push(
        {
          field: 'meterStartReading',
          label: 'Start Reading',
          type: 'number',
          size: 4,
        },
        {
          field: 'meterUtilisation',
          label: 'Utilisation',
          type: 'number',
          size: 4,
          mustBeGreaterThanZero: true,
        },
        {
          field: 'empty3',
          label: '',
          type: 'empty',
          size: 4,
        },
        {
          field: 'lifeInMeterUnits',
          label: `Life in ${meterDetails?.measurementUnits?.unitName || 'Units'
            }`,
          type: 'number',
          size: 4,
        }
      );
    } else {
      fields.push(
        {
          field: 'empty1',
          label: '',
          type: 'empty',
          size: 4,
        },
        {
          field: 'empty2',
          label: '',
          type: 'empty',
          size: 4,
        },
      );
    }
    return fields;
  }, [
    assetTypeOptions,
    planningTypeOptions,
    handleGroupSearch,
    handleGroupChange,
    selectedGroup,
    defaultMeterId,
    meterDetails?.measurementUnits?.unitName,
  ]);

  const { errors, validate } = useValidator(commonFields, formData);
  const { renderField: renderCommonField } = useFormBuilder(commonFields, formData, setFormData, errors);
  let clearedEndDateError = false;
  const handleRowChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const updatedRow = { ...next[index], [field]: value };

      if (field === 'assetStartDate') {
        const calculated = calculateEndDateFromLife(value);
        if (calculated) {
          updatedRow.assetEndDate = calculated;
          clearedEndDateError = true;
        }
      }
      if (field === 'assetEndDate') {
        updatedRow.assetEndDate = value;
      }
      next[index] = updatedRow;
      return next;
    });
    setRowErrors((prev) => {
      const next = [...prev];
      const updatedErrors = { ...(next[index] || {}) };
      delete updatedErrors[field];
      if (clearedEndDateError) {
        delete updatedErrors.assetEndDate;
      }
      next[index] = updatedErrors;
      return next;
    });
  };

  const handleAddRow = useCallback(() => {
    const newRow = buildRow(getTodayDateString());
    const hasLifeValue = Number(formData.lifeInMeterUnits) > 0;
    const utilisationValue = Number(formData.meterUtilisation || defaultUtilisation || 0);
    const shouldAutoCalculateEndDate =
      !!defaultMeterId && hasLifeValue && Number.isFinite(utilisationValue) && utilisationValue > 0;
    if (shouldAutoCalculateEndDate) {
      const calculated = calculateEndDateFromLife(newRow.assetStartDate);
      if (calculated) {
        newRow.assetEndDate = calculated;
      }
    }
    setRows((prev) => [...prev, newRow]);
    setRowErrors((prev) => [...prev, {}]);
  }, [
    calculateEndDateFromLife,
    defaultMeterId,
    defaultUtilisation,
    formData.lifeInMeterUnits,
    formData.meterUtilisation,
  ]);

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, idx) => idx !== index));
    setRowErrors((prev) => prev.filter((_, idx) => idx !== index));
  };

  const validateRows = useCallback(() => {
    const nextErrors = rows.map(() => ({}));
    let isValid = true;
    const serialCounts = {};
    if (noDuplicateSerial) {
      rows.forEach((row) => {
        const trimmed = row.assetSerial?.trim();
        if (trimmed) {
          serialCounts[trimmed] = (serialCounts[trimmed] || 0) + 1;
        }
      });
    }
    rows.forEach((row, index) => {
      const rowErr = {};
      if (!row.assetNumber || !row.assetNumber.trim()) {
        rowErr.assetNumber = 'Asset number is required';
      } else if (/\s/.test(row.assetNumber)) {
        rowErr.assetNumber = 'Asset number cannot contain spaces';
      }
      if (serialRequired && (!row.assetSerial || !row.assetSerial.trim())) {
        rowErr.assetSerial = 'Serial number is required';
      }
      if (noDuplicateSerial) {
        const trimmedSerial = row.assetSerial?.trim();
        if (trimmedSerial && serialCounts[trimmedSerial] > 1) {
          rowErr.assetSerial = 'Serial number duplicated in this list';
        }
      }
      if (!row.assetStartDate) {
        rowErr.assetStartDate = 'Start date is required';
      }
      if (!row.assetEndDate) {
        rowErr.assetEndDate = 'End date is required';
      }
      if (row.assetStartDate && row.assetEndDate) {
        const start = new Date(row.assetStartDate);
        const end = new Date(row.assetEndDate);
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end <= start) {
          rowErr.assetEndDate = 'End date must be after the start date';
        }
      }
      if (Object.keys(rowErr).length > 0) {
        isValid = false;
      }
      nextErrors[index] = rowErr;
    });
    setRowErrors(nextErrors);
    return isValid;
  }, [rows, serialRequired, noDuplicateSerial]);

  const validateSerialsRemotely = useCallback(async () => {
    if (!noDuplicateSerial) return true;
    let valid = true;
    const remoteErrors = rows.map(() => ({}));
    for (let i = 0; i < rows.length; i += 1) {
      const currentRow = rows[i];
      const trimmedSerial = currentRow.assetSerial?.trim();
      if (!trimmedSerial) continue;
      try {
        const duplicate = await getAssetBySerial(trimmedSerial);
        if (duplicate) {
          remoteErrors[i] = {
            ...remoteErrors[i],
            assetSerial: 'Serial number already exists',
          };
          valid = false;
        }
      } catch (err) {
        console.error('Error checking serial number:', err);
      }
    }
    if (!valid) {
      setRowErrors((prev) =>
        rows.map((_, idx) => ({
          ...(prev[idx] || {}),
          ...(remoteErrors[idx] || {}),
        })),
      );
    }
    return valid;
  }, [noDuplicateSerial, rows]);

  const resetStateAfterSave = useCallback(() => {
    setRows([buildRow(getTodayDateString())]);
    setRowErrors([{}]);
    setFormData((prev) => ({
      ...prev,
      assetDescription: '',
      assetImage: '',
    }));
    setSelectedGroup(null);
    setPendingPayloads([]);
    setPendingMeterConfig({ startReading: 0, utilisation: 0 });
    setConfirmationRows([]);
    setShowConfirmationModal(false);
  }, []);

  const handleSave = async () => {
    const isCommonValid = validate();
    const rowsValid = validateRows();
    if (!isCommonValid || !rowsValid) {
      return;
    }
    setIsValidating(true);
    try {
      const serialsValid = await validateSerialsRemotely();
      if (!serialsValid) {
        return;
      }
      const trimmedGroupName = selectedGroup?.label || '';
      const departmentName =
        selectedDepartment?.name ||
        selectedDepartment?.hierarchyName ||
        selectedDepartment?.hierarchyStructureName ||
        '';
      const meterStartReading = Number(formData.meterStartReading) || 0;
      const meterUtilisation =
        Number(formData.meterUtilisation || defaultUtilisation) || 0;
      const payloads = rows.map((currentRow) => {
        const trimmedNumber = currentRow.assetNumber?.trim() || '';
        const trimmedSerial = currentRow.assetSerial?.trim() || '';
        const assetDescription = (formData.assetDescription || trimmedGroupName).trim();
        const payload = {
          assetNumber: trimmedNumber,
          assetSerial: trimmedSerial,
          assetDescription,
          plantStatusId: toNumericOrNull(formData.plantStatusId) ?? 6,
          assetCriticalityId: toNumericOrNull(formData.assetCriticalityId) ?? 2,
          costCenterId: toNumericOrNull(formData.costCenterId),
          assetDepartmentId: toNumericOrNull(formData.assetDepartmentId),
          PlanningTypeId: toNumericOrNull(formData.PlanningTypeId),
          AssetTypeId: toNumericOrNull(formData.AssetTypeId),
          assetStartDate: normalizeDate(currentRow.assetStartDate),
          assetEndDate: normalizeDate(currentRow.assetEndDate),
          organisationId: userProfile?.org_id,
          createdAt: new Date().toISOString(),
          createdBy: userProfile?.id,
          assetImage: formData.assetImage || null,
        };

        if (selectedGroup?.isLocal === false) {
          payload.globalGroupId = formData.groupId;
        } else {
          payload.groupId = formData.groupId;
        }

        return {
          payload,
          assetStartDate: normalizeDate(currentRow.assetStartDate),
        };
      });

      const previewRows = payloads.map((entry) => ({
        assetNumber: entry.payload.assetNumber,
        assetSerial: entry.payload.assetSerial,
        groupName: trimmedGroupName,
        departmentName,
        assetStartDate: entry.payload.assetStartDate,
        assetEndDate: entry.payload.assetEndDate,
      }));

      setPendingPayloads(payloads);
      setPendingMeterConfig({
        startReading: meterStartReading,
        utilisation: meterUtilisation,
      });
      setConfirmationRows(previewRows);
      setShowConfirmationModal(true);
    } catch (err) {
      console.error('Error validating assets before save:', err);
      showToastError('Unable to validate assets. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!pendingPayloads.length) {
      setShowConfirmationModal(false);
      return;
    }
    setSaving(true);
    try {
      for (let i = 0; i < pendingPayloads.length; i += 1) {
        const entry = pendingPayloads[i];
        const created = await createAsset(entry.payload);

        if (defaultMeterId) {
          const startReading = pendingMeterConfig.startReading || 0;
          const utilisation = pendingMeterConfig.utilisation || 0;
          await createAssetMeter({
            assetId: created.id,
            meterId: defaultMeterId,
            startReading,
            utilisation,
            primaryMeter: 1,
          });
          if (entry.assetStartDate) {
            await createMeterReadings({
              assetId: created.id,
              metersId: defaultMeterId,
              value: startReading,
              readingDate: new Date(entry.assetStartDate).toISOString(),
            });
          }
        }
      }
      clearCache('groups');
      showToastSuccess('Assets added successfully.');
      resetStateAfterSave();
      if (onSaved) {
        await onSaved();
      }
      onClose && onClose();
    } catch (err) {
      console.error('Error bulk adding assets:', err);
      showToastError('Unable to add assets. Please try again.');
    } finally {
      setSaving(false);
      setShowConfirmationModal(false);
    }
  };

  const handleCancelConfirmation = () => {
    if (saving) return;
    setShowConfirmationModal(false);
    setConfirmationRows([]);
    setPendingPayloads([]);
    setPendingMeterConfig({ startReading: 0, utilisation: 0 });
  };

  if (!show) return null;

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Bulk Add Assets</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving || isValidating} />
            </div>
            <div className="modal-body">
              <p className="fw-semibold mt-16 mb-16">Enter common information for the new Assets</p>
              <div className="row g-3">
                {commonFields.map(renderCommonField)}
              </div>
              <hr className="mt-32" />
              <p className="fw-semibold mt-16 mb-12">Add one or more assets</p>
              <div className="d-flex flex-column gap-3">
                {rows.map((row, index) => {
                  const rowErr = rowErrors[index] || {};
                  return (
                    <div className="row g-3 align-items-end" key={`asset-row-${index}`}>
                      <div className="col-md-3">
                        <label className="form-label">Asset Number *</label>
                        <input
                          type="text"
                          className={`form-control ${rowErr.assetNumber ? 'is-invalid' : ''}`}
                          value={row.assetNumber}
                          onChange={(e) => handleRowChange(index, 'assetNumber', e.target.value)}
                        />
                        {rowErr.assetNumber && (
                          <div className="invalid-feedback">{rowErr.assetNumber}</div>
                        )}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">
                          Serial Number{serialRequired ? ' *' : ''}
                        </label>
                        <input
                          type="text"
                          className={`form-control ${rowErr.assetSerial ? 'is-invalid' : ''}`}
                          value={row.assetSerial}
                          onChange={(e) => handleRowChange(index, 'assetSerial', e.target.value)}
                        />
                        {rowErr.assetSerial && (
                          <div className="invalid-feedback">{rowErr.assetSerial}</div>
                        )}
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Start Date *</label>
                        <input
                          type="date"
                          className={`form-control ${rowErr.assetStartDate ? 'is-invalid' : ''}`}
                          value={row.assetStartDate}
                          onChange={(e) => handleRowChange(index, 'assetStartDate', e.target.value)}
                        />
                        {rowErr.assetStartDate && (
                          <div className="invalid-feedback">{rowErr.assetStartDate}</div>
                        )}
                      </div>
                      <div className="col-md-3 d-flex align-items-end gap-2">
                        <div className="flex-grow-1">
                          <label className="form-label">End Date *</label>
                          <input
                            type="date"
                            className={`form-control ${rowErr.assetEndDate ? 'is-invalid' : ''}`}
                            value={row.assetEndDate}
                            onChange={(e) => handleRowChange(index, 'assetEndDate', e.target.value)}
                          />
                          {rowErr.assetEndDate && (
                            <div className="invalid-feedback">{rowErr.assetEndDate}</div>
                          )}
                        </div>
                        {index > 0 && (
                          <button
                            type="button"
                            className="delete-btn w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleRemoveRow(index)}
                            aria-label="Delete asset row"
                          >
                            <Icon icon="ic:baseline-delete-outline" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <button type="button" className="btn btn-link text-decoration-none px-0 mt-12" onClick={handleAddRow} disabled={saving || isValidating}>
                + Add Another Asset
              </button>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose} disabled={saving || isValidating}>
                Close
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || isValidating}>
                {isValidating ? 'Validating...' : 'Save Assets'}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showConfirmationModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Assets</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelConfirmation}
                  disabled={saving}
                />
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">The folloiwng assets will be created.</p>
                <div className="table-responsive">
                  <table className="table bordered-table">
                    <thead>
                      <tr>
                        <th>Asset Number</th>
                        <th>Serial Number</th>
                        <th>Group</th>
                        <th>Department</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {confirmationRows.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-3">
                            No assets to display
                          </td>
                        </tr>
                      ) : (
                        confirmationRows.map((row, idx) => (
                          <tr key={`${row.assetNumber || idx}-${idx}`}>
                            <td>{row.assetNumber || '—'}</td>
                            <td>{row.assetSerial || '—'}</td>
                            <td>{row.groupName || '—'}</td>
                            <td>{row.departmentName || '—'}</td>
                            <td>{row.assetStartDate || '—'}</td>
                            <td>{row.assetEndDate || '—'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCancelConfirmation} disabled={saving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleConfirmSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Ok'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetBulkAddModal;